import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, Button, Field, Input } from "@pracownik/ui";
import { loginSchema, type LoginInput } from "@pracownik/shared/schemas";
import { signIn } from "@/lib/auth-client";
import { GOOGLE_NOT_INVITED_KEY, useOAuthErrorKey } from "@/lib/oauth-error";
import { AuthLayout } from "@/layouts/auth-layout";
import { GoogleIcon } from "@/components/google-icon";

function safeNext(raw: string | null): string {
  // Only same-origin paths — never an absolute URL from the query string.
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/dashboard";
}

export function LoginPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // A failed Google round-trip lands back here as ?error=<CODE> (see handleGoogleLogin).
  const oauthKey = useOAuthErrorKey();

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: LoginInput) => {
    setLoading(true);
    try {
      const result = await signIn.email({ email: data.email, password: data.password });
      if (result.error) {
        // requireEmailVerification=true on the server: an unverified account can
        // never log in until they click the link. BetterAuth re-sends the email
        // on this code path, so route to the "check your inbox" page.
        const code = (result.error as { code?: string; status?: number }).code;
        const status = (result.error as { status?: number }).status;
        if (code === "EMAIL_NOT_VERIFIED" || status === 403) {
          navigate(`/verify-email-sent?email=${encodeURIComponent(data.email)}`);
          return;
        }
        toast.error(t("loginFailed"));
        return;
      }
      navigate(next);
    } catch {
      toast.error(t("loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      // Without errorCallbackURL BetterAuth strands the user on its raw API error page.
      const errorCallbackURL = new URL("/login", window.location.origin);
      if (params.has("next")) errorCallbackURL.searchParams.set("next", next);
      await signIn.social({
        provider: "google",
        callbackURL: `${window.location.origin}${next}`,
        errorCallbackURL: errorCallbackURL.toString(),
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <AuthLayout title={t("login")} description={t("loginDescription")}>
      {oauthKey && (
        <Alert variant="destructive" className="mb-5">
          <AlertDescription>
            {t(oauthKey)}
            {oauthKey === GOOGLE_NOT_INVITED_KEY && (
              <>
                {" "}
                <Link to="/signup" className="font-medium underline underline-offset-2">
                  {t("signUp")}
                </Link>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Field
          htmlFor="email"
          label={t("email")}
          error={form.formState.errors.email && t("invalidEmail")}
        >
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="name@example.com"
            autoFocus
            {...form.register("email")}
          />
        </Field>
        <Field
          htmlFor="password"
          label={t("password")}
          error={form.formState.errors.password && t("passwordTooShort")}
          optional={
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-primary hover:underline"
            >
              {t("forgotPassword")}
            </Link>
          }
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            {...form.register("password")}
          />
        </Field>

        <Button type="submit" className="w-full" size="lg" loading={loading}>
          {t("login")}
        </Button>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-card px-3 text-xs text-text-tertiary">{t("orContinueWith")}</span>
        </div>
      </div>

      <Button
        variant="outline"
        size="lg"
        className="w-full"
        onClick={handleGoogleLogin}
        loading={googleLoading}
      >
        <GoogleIcon className="size-4" />
        {t("continueWithGoogle")}
      </Button>

      <p className="mt-6 text-center text-sm text-text-secondary">
        {t("noAccount")}{" "}
        <Link
          to={`/signup${params.get("invite") ? `?invite=${encodeURIComponent(params.get("invite")!)}` : ""}`}
          className="font-medium text-primary hover:underline"
        >
          {t("signUp")}
        </Link>
      </p>
    </AuthLayout>
  );
}
